import React, { useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

import LoginViewport from '../components/LoginViewport';
import SvgLoading from '../components/SvgLoading';
import HttpClient from '../utils/HttpClient';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


export default function ForgotPassword() {
  const cookies = new Cookies(null, { path: '/' });
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidEmail = useMemo(() => {
    if (!email) return false;
    return EMAIL_REGEX.test(email.trim());
  }, [email]);

  const handleEmailChange = useCallback((event) => {
    setEmail(event.target.value);
  }, []);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();
    if (!isValidEmail) {
      toast.error(t('Login:Invalid email'));
      return;
    }
    setIsSubmitting(true);

    try {
      const trimmedEmail = email.trim().toLowerCase();

      const formData = {
        email: trimmedEmail,
        url: window.location.href,
        path: 'login/reset-password/',
      };

      await HttpClient.post(
        '/forgot/',
        formData,
        cookies.get('token')
      );

      toast.success(
        `${t('Login:An email to reset your password')} ${trimmedEmail}`,
        { duration: 6000 }
      );

      setTimeout(() => {
          navigate('/login', { replace: true });
        }, 1000);
    } catch (error) {
        console.error('Error requesting password reset:', error);
        toast.error(t('Common:Server Error'));
    } finally {
      setIsSubmitting(false);
    }
  }, [email, isValidEmail, navigate, t, cookies]);

  return (
    <LoginViewport>
      <div className="w-full">
        <div className="lg:hidden flex justify-center mb-10">
          <img
            className="h-12 w-auto"
            src="/public/assets/logo-harpiAI.svg"
            alt="HarpiAI"
          />
        </div>

        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            {t('Login:Forgot password')}?
          </h2>
          <p className="mt-2 text-md text-white">
            {t('Login:Enter your email')}
          </p>
        </div>

        <div className="mt-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mt-5">
              <label htmlFor="email" className="block text-sm font-medium leading-6 text-white mb-2">
                {t('Login:Email')}
              </label>

              <div className="relative">
                <input
                  type="email"
                  name="email"
                  id="email"
                  value={email}
                  className="block w-full rounded-xl border-0 py-3.5 px-4 bg-white/5 text-white shadow-sm ring-1 ring-inset ring-white/10 placeholder:text-slate-500 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 transition-all duration-200"
                  onChange={handleEmailChange}
                  disabled={isSubmitting}
                  autoComplete="email"
                  required
                />
              </div>
            </div>

            <div className="mt-8">
              <button
                disabled={isSubmitting || !isValidEmail}
                type="submit"
                className="flex w-full justify-center rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3.5 text-sm font-semibold text-white shadow-md hover:from-blue-500 hover:to-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform active:scale-[0.98]"
              >
                {isSubmitting ? <SvgLoading /> : t('Login:Submit')}
              </button>
            </div>

            <p className="mt-6 text-white text-center text-sm text-slate-400">
              {t('Login:Remember password')}?{' '}
              <Link
                to="/login"
                className="font-semibold text-blue-400 hover:text-blue-300 transition-colors"
              >
                {t('Login:Back')}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </LoginViewport>
  );
};
